import * as XLSX from 'xlsx-js-style';
import { datedFilename } from '../../utils/excelExport';

/**
 * The quiz template workbook — one file that both TELLS a conversational AI
 * assistant how to write a paper and IS the file this app reads back
 * (docs/modules/ld.md §5.4).
 *
 * Three sheets:
 *   Instructions   what the file is for, how to use it, and the prompt to
 *                  hand the assistant (the prompt repeats every rule, so it
 *                  still works pasted on its own without the attachment)
 *   Questions      the sheet to fill in: one question per row, Option A–J
 *   Column Guide   the same rules as a table, for whoever fills it in by hand
 *
 * The parser is deliberately forgiving — an assistant will not reproduce the
 * header row to the letter: column names are matched loosely, option gaps are
 * closed up, and a correct answer may be given as a letter, a number or the
 * option text itself. What it does NOT do is guess past the paper's rules:
 * the caller runs the editor's own validation on what comes out, so a sloppy
 * file is reported row by row instead of importing half a paper.
 */

// Option A–J: the editor's ten-option ceiling, as fixed columns.
const OPTION_LETTERS = 'ABCDEFGHIJ'.split('');

const SHEET_INSTRUCTIONS = 'Instructions';
const SHEET_QUESTIONS = 'Questions';
const SHEET_GUIDE = 'Column Guide';

const HEADERS = [
  'Type',
  'Question',
  ...OPTION_LETTERS.map((l) => `Option ${l}`),
  'Correct Answers',
  'Points',
  'Must Answer Correctly',
];

// Rows carrying this prefix are samples, not questions — they stay in the
// file (an assistant writes better with an example in front of it) and are
// skipped on import.
const EXAMPLE_PREFIX = 'EXAMPLE:';

// ── Template ────────────────────────────────────────────────────────────────

const BORDER = { style: 'thin', color: { rgb: 'BFBFBF' } };
const BORDERS = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: '1F2937' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'D9E1F2' } },
  alignment: { vertical: 'center', wrapText: true },
  border: BORDERS,
};
const BODY_STYLE = { alignment: { vertical: 'top', wrapText: true }, border: BORDERS };
const SAMPLE_STYLE = {
  alignment: { vertical: 'top', wrapText: true },
  border: BORDERS,
  font: { italic: true, color: { rgb: '64748B' } },
};
const TITLE_STYLE = { font: { bold: true, sz: 16, color: { rgb: '1F2937' } } };
const SUBTITLE_STYLE = { font: { sz: 11, color: { rgb: '64748B' } } };
const SECTION_STYLE = { font: { bold: true, sz: 12, color: { rgb: '1F2937' } } };
const TEXT_STYLE = { alignment: { vertical: 'top', wrapText: true } };
const PROMPT_STYLE = {
  alignment: { vertical: 'top', wrapText: true },
  fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
  font: { name: 'Consolas', sz: 10, color: { rgb: '0F172A' } },
};

// The prompt is the heart of the file: it carries the whole format, so the
// assistant produces a loadable sheet whether or not it can open the
// attachment. Placeholders in <angle brackets> are for the user to replace.
const PROMPT_LINES = [
  'You are helping me write a post-course knowledge check for staff at an accounting firm.',
  '',
  'Course / topic: <describe what the course covered, or attach the course materials>',
  'Number of questions: <e.g. 10>',
  'Audience: <e.g. audit staff, all levels>',
  '',
  'Fill in the "Questions" sheet of the attached workbook: one question per row, starting at row 2.',
  'Keep the sheet name, the header row and the column order exactly as they are. Do not add, remove',
  'or rename any column or sheet. The two EXAMPLE rows may stay where they are — they are ignored',
  'when the file is read back.',
  '',
  'Column rules:',
  '- Type: "Single" when exactly one option is correct, "Multiple" when more than one is.',
  '- Question: the question text, up to 2000 characters, on a single line.',
  '- Option A to Option J: between 2 and 10 options. Fill them in order from Option A.',
  '- Correct Answers: the letters of the correct options, separated by commas — "B" for a single-choice',
  '  question, "A, C" for a multiple-choice one. A participant has to pick every correct option to',
  '  score a multiple-choice question, so do not mark an option correct unless it clearly is.',
  '- Points: a whole number from 1 to 100. Use 1 unless a question deserves more weight.',
  '- Must Answer Correctly: "Yes" when getting this question wrong should fail the whole quiz whatever',
  '  the score; otherwise "No".',
  '',
  'Writing rules:',
  '- Every question must be answerable from the course material alone, with one defensible answer key.',
  '- Write plausible distractors and keep the options roughly the same length, so the answer is not',
  '  obvious from the shape of the text.',
  '- Do not use "All of the above" or "None of the above", and do not hint at the answer in the wording.',
  '- Spread the correct answers across the letters instead of favouring one.',
  '',
  'Give me back ONE file only: the completed workbook. Do not also send a CSV, a copy of the table in',
  'the chat, or a summary of what you wrote. Only if you cannot produce a file at all, send the',
  'Questions sheet as a CSV table with the same header row instead — a .csv file can be imported too.',
];

/**
 * The same prompt as plain text, for the editor's Copy Prompt button — the
 * workbook and the clipboard must never drift apart, so both read this list.
 */
export const QUIZ_PROMPT = PROMPT_LINES.join('\n');

const instructionRows = () => [
  { text: 'Quiz Question Template', style: TITLE_STYLE },
  { text: 'PKF Dashboard Portal — Learning & Development, post-course quiz', style: SUBTITLE_STYLE },
  { text: '' },
  { text: 'What This File Is For', style: SECTION_STYLE },
  { text: 'Writing a paper from scratch takes a while, so this workbook lets a conversational AI assistant draft it for you. It carries the format the assistant should follow and the layout this app reads back, so the questions it writes land straight in the quiz editor.' },
  { text: '' },
  { text: 'How to Use It', style: SECTION_STYLE },
  { text: '1. Open a conversational AI assistant, attach this file and paste the prompt below (the quiz editor’s Copy Prompt button puts it on the clipboard for you).' },
  { text: '2. Replace the <angle bracket> placeholders in the prompt with your course and the number of questions you want.' },
  { text: '3. Save the workbook it gives back (.xlsx, .xlsm or .csv).' },
  { text: '4. In the quiz editor, press Import from Excel and pick that file.' },
  { text: '5. Read every question and answer on screen and fix what needs fixing. Nothing is recorded until you press Save.' },
  { text: '' },
  { text: 'The assistant drafts; you are the author. Check each answer key against the course material, and do not paste confidential client information into a public AI assistant.', style: { ...TEXT_STYLE, font: { italic: true, color: { rgb: '92400E' } } } },
  { text: '' },
  { text: 'The Prompt — Copy Everything Below', style: SECTION_STYLE },
  ...PROMPT_LINES.map((text) => ({ text, style: PROMPT_STYLE })),
  { text: '' },
  { text: 'Then fill in the Questions sheet. The Column Guide sheet says what each column accepts.', style: SUBTITLE_STYLE },
];

const buildInstructionsSheet = () => {
  const rows = instructionRows();
  const ws = {};
  rows.forEach((row, i) => {
    ws[XLSX.utils.encode_cell({ r: i, c: 0 })] = {
      t: 's', v: row.text, s: row.style || TEXT_STYLE,
    };
  });
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: 0 } });
  ws['!cols'] = [{ wch: 108 }];
  return ws;
};

const SAMPLES = [
  {
    Type: 'Single',
    Question: `${EXAMPLE_PREFIX} Under SSA 220 (Revised), who is ultimately responsible for managing and achieving quality on an audit engagement?`,
    'Option A': 'The engagement partner',
    'Option B': 'The engagement quality reviewer',
    'Option C': 'The client’s audit committee',
    'Option D': 'The firm’s quality management function',
    'Correct Answers': 'A',
    Points: 1,
    'Must Answer Correctly': 'No',
  },
  {
    Type: 'Multiple',
    Question: `${EXAMPLE_PREFIX} Which of the following must be on the audit file before the report is signed?`,
    'Option A': 'The engagement letter signed by the client',
    'Option B': 'Significant judgements and how they were resolved',
    'Option C': 'The engagement quality review, where one is required',
    'Option D': 'The client’s marketing plan for the coming year',
    'Correct Answers': 'A, B, C',
    Points: 2,
    'Must Answer Correctly': 'Yes',
  },
];

const QUESTION_COL_WIDTHS = {
  Type: 12,
  Question: 62,
  'Correct Answers': 16,
  Points: 8,
  'Must Answer Correctly': 20,
};

const buildQuestionsSheet = () => {
  const ws = {};
  HEADERS.forEach((h, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: h, s: HEADER_STYLE };
  });
  SAMPLES.forEach((sample, i) => {
    HEADERS.forEach((h, c) => {
      const raw = sample[h];
      const ref = XLSX.utils.encode_cell({ r: i + 1, c });
      ws[ref] = typeof raw === 'number'
        ? { t: 'n', v: raw, s: SAMPLE_STYLE }
        : { t: 's', v: raw === undefined ? '' : String(raw), s: SAMPLE_STYLE };
    });
  });
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: SAMPLES.length, c: HEADERS.length - 1 } });
  ws['!cols'] = HEADERS.map((h) => ({ wch: QUESTION_COL_WIDTHS[h] || 26 }));
  ws['!rows'] = [{ hpt: 30 }];
  return ws;
};

const GUIDE_ROWS = [
  {
    Column: 'Type',
    Required: 'Yes',
    'Accepted Values': 'Single, Multiple',
    Notes: 'Single = exactly one correct option; Multiple = more than one. Left blank, it is taken from how many answers are marked correct.',
  },
  {
    Column: 'Question',
    Required: 'Yes',
    'Accepted Values': 'Text, up to 2,000 characters',
    Notes: `One question per row. A row whose question starts with "${EXAMPLE_PREFIX}" is a sample and is skipped on import.`,
  },
  {
    Column: 'Option A – Option J',
    Required: 'At least two',
    'Accepted Values': 'Text, up to 2,000 characters',
    Notes: 'Fill them in order from Option A. A gap in the middle is closed up on import, so the letters in Correct Answers always mean the column they are written in.',
  },
  {
    Column: 'Correct Answers',
    Required: 'Yes',
    'Accepted Values': 'Option letters, e.g. B or A, C',
    Notes: 'Single choice: exactly one letter. Multiple choice: one or more, and the participant has to pick all of them to score. The option text itself is also accepted in place of a letter.',
  },
  {
    Column: 'Points',
    Required: 'No',
    'Accepted Values': 'Whole number, 1 to 100',
    Notes: 'Blank counts as 1. The pass mark is a percentage of the paper’s total points, so a heavier question weighs more.',
  },
  {
    Column: 'Must Answer Correctly',
    Required: 'No',
    'Accepted Values': 'Yes, No',
    Notes: 'Blank counts as No. Yes marks a critical question: whoever gets it wrong is Not passed even if their score reaches the pass mark.',
  },
];

const GUIDE_HEADERS = ['Column', 'Required', 'Accepted Values', 'Notes'];
const GUIDE_WIDTHS = [22, 12, 30, 72];

const buildGuideSheet = () => {
  const ws = {};
  GUIDE_HEADERS.forEach((h, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: h, s: HEADER_STYLE };
  });
  GUIDE_ROWS.forEach((row, i) => {
    GUIDE_HEADERS.forEach((h, c) => {
      ws[XLSX.utils.encode_cell({ r: i + 1, c })] = { t: 's', v: String(row[h] || ''), s: BODY_STYLE };
    });
  });
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: GUIDE_ROWS.length, c: GUIDE_HEADERS.length - 1 } });
  ws['!cols'] = GUIDE_WIDTHS.map((wch) => ({ wch }));
  ws['!rows'] = [{ hpt: 24 }];
  return ws;
};

/** Build the template and hand it to the browser as a download. */
export const downloadQuizTemplate = () => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildInstructionsSheet(), SHEET_INSTRUCTIONS);
  XLSX.utils.book_append_sheet(wb, buildQuestionsSheet(), SHEET_QUESTIONS);
  XLSX.utils.book_append_sheet(wb, buildGuideSheet(), SHEET_GUIDE);
  XLSX.writeFile(wb, datedFilename('Quiz_Question_Template.xlsx'));
};

// ── Reading a filled-in file ────────────────────────────────────────────────

const norm = (v) => String(v === null || v === undefined ? '' : v).toLowerCase().replace(/[^a-z0-9]/g, '');
const text = (v) => String(v === null || v === undefined ? '' : v).trim();

const COLUMN_ALIASES = {
  type: ['type', 'questiontype', 'answertype', 'choicetype'],
  question: ['question', 'questiontext', 'questions', 'text'],
  correct: ['correctanswers', 'correctanswer', 'correct', 'answer', 'answers', 'answerkey', 'correctoption', 'correctoptions', 'key'],
  points: ['points', 'point', 'marks', 'mark', 'weight', 'score'],
  mustPass: ['mustanswercorrectly', 'mustpass', 'mustbecorrect', 'critical', 'criticalquestion', 'mandatory'],
};

// 'Option C' / 'option3' / 'C' / 'Choice B' → the letter index, or -1.
const optionIndexOf = (header) => {
  const n = norm(header);
  let m = n.match(/^(?:option|opt|choice|answer)?([a-j])$/);
  if (m) return OPTION_LETTERS.indexOf(m[1].toUpperCase());
  m = n.match(/^(?:option|opt|choice|answer)(\d{1,2})$/);
  if (m) {
    const idx = Number(m[1]) - 1;
    return idx >= 0 && idx < OPTION_LETTERS.length ? idx : -1;
  }
  return -1;
};

// The template's own names win over the looser aliases, so a file carrying
// both (a 'Text' column beside a 'Question' one) is read the obvious way.
const mapColumns = (headerRow) => {
  const cols = { type: -1, question: -1, correct: -1, points: -1, mustPass: -1 };
  const options = new Array(OPTION_LETTERS.length).fill(-1);
  const claim = (names) => headerRow.forEach((cell, c) => {
    const n = norm(cell);
    if (!n) return;
    const key = Object.keys(COLUMN_ALIASES).find((k) => cols[k] === -1 && names(k).includes(n));
    if (key) cols[key] = c;
  });
  claim((k) => [COLUMN_ALIASES[k][0]]);
  claim((k) => COLUMN_ALIASES[k]);
  headerRow.forEach((cell, c) => {
    if (!norm(cell) || Object.values(cols).includes(c)) return;
    const oi = optionIndexOf(cell);
    if (oi !== -1 && options[oi] === -1) options[oi] = c;
  });
  return { ...cols, options };
};

const TYPE_SINGLE = ['single', 'singlechoice', 'singleanswer', 'one', 'oneanswer', 's', '1'];
const TYPE_MULTIPLE = ['multiple', 'multi', 'multiplechoice', 'multipleanswer', 'multiplechoices', 'many', 'm'];
const TRUTHY = ['yes', 'y', 'true', 't', '1', 'required', 'critical', 'mustpass'];

/**
 * The Correct Answers cell → the positions of the marked options within
 * `options` (which holds the row's filled-in options, each keeping the letter
 * of the column it came from — so a gap in the middle never shifts a letter).
 * A token may be a letter (B), a column number (2), or the option's own text.
 * @returns {{picked: number[], unknown: string[]}}
 */
const resolveCorrect = (cell, options) => {
  const picked = [];
  const unknown = [];
  const addLetter = (letter) => {
    const i = options.findIndex((o) => o.letter === letter);
    if (i === -1) return false;
    if (!picked.includes(i)) picked.push(i);
    return true;
  };
  const whole = text(cell);
  if (!whole) return { picked, unknown };
  // 'A', 'A, C', 'A C', 'a and c' — bare letters, however they are separated.
  if (/^[a-j](?:[\s,;/|&]+(?:and[\s]+)?[a-j])*$/i.test(whole.replace(/\.$/, ''))) {
    whole.replace(/\band\b/gi, ' ').match(/[a-j]/gi).forEach((l) => {
      const letter = l.toUpperCase();
      if (!addLetter(letter)) unknown.push(letter);
    });
    return { picked, unknown };
  }
  // Anything else is split on real separators only, so an answer spelled out
  // in full ('Option C', or the option's own wording) survives as one token.
  whole.split(/[,;/|\n]/).map((t) => t.trim()).filter(Boolean).forEach((token) => {
    const bare = token.replace(/^[(]|[).:]+$/g, '').trim();
    const n = norm(bare);
    const asLetter = n.match(/^(?:option|opt|choice|answer)?([a-j])$/);
    if (asLetter) {
      if (!addLetter(asLetter[1].toUpperCase())) unknown.push(bare);
      return;
    }
    const asNumber = n.match(/^(?:option|opt|choice|answer)?(\d{1,2})$/);
    if (asNumber) {
      const letter = OPTION_LETTERS[Number(asNumber[1]) - 1];
      if (!letter || !addLetter(letter)) unknown.push(bare);
      return;
    }
    // Spelled out in full: match the option text, letter prefix and all.
    const byText = options.findIndex((o) => {
      const t = norm(o.text);
      return t && (t === n || norm(`${o.letter}${o.text}`) === n || norm(`${o.letter}.${o.text}`) === n);
    });
    if (byText !== -1) {
      if (!picked.includes(byText)) picked.push(byText);
      return;
    }
    unknown.push(bare);
  });
  return { picked, unknown };
};

const uid = () => Math.random().toString(36).slice(2);

/**
 * Read a filled-in template (.xlsx / .xlsm / .csv) into editor-shape questions.
 *
 * Structural trouble throws (no readable sheet, no header row, no rows);
 * per-row trouble comes back in `problems` naming the spreadsheet row, so the
 * caller can refuse the whole import rather than load half a paper.
 *
 * @param {File} file
 * @returns {Promise<{questions: Array, rowNumbers: number[], skipped: number,
 *   sheetName: string, problems: string[]}>}
 */
export const parseQuizWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  let wb;
  try {
    wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
  } catch (e) {
    throw new Error('This file could not be read as a spreadsheet — save it as .xlsx or .csv and try again.');
  }
  const names = wb.SheetNames || [];
  if (names.length === 0) throw new Error('The workbook has no sheets.');

  // The template's own sheet first; otherwise the first sheet that has a
  // question column — an assistant may hand back a single unnamed sheet.
  const gridOf = (name) => XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, defval: '' });
  const headerRowIn = (grid) => {
    for (let i = 0; i < Math.min(grid.length, 20); i += 1) {
      if ((grid[i] || []).some((c) => COLUMN_ALIASES.question.includes(norm(c)))) return i;
    }
    return -1;
  };
  let sheetName = names.find((n) => norm(n) === norm(SHEET_QUESTIONS));
  let grid = sheetName ? gridOf(sheetName) : null;
  let headerIdx = grid ? headerRowIn(grid) : -1;
  if (headerIdx === -1) {
    sheetName = names.find((n) => headerRowIn(gridOf(n)) !== -1);
    if (!sheetName) {
      throw new Error('No question table found — the file needs a header row with a "Question" column. Download the template and keep its header row.');
    }
    grid = gridOf(sheetName);
    headerIdx = headerRowIn(grid);
  }

  const cols = mapColumns(grid[headerIdx] || []);
  if (cols.question === -1) throw new Error('The "Question" column is missing.');
  if (cols.options.every((c) => c === -1)) {
    throw new Error('No option columns found — the file needs "Option A", "Option B" and so on.');
  }

  const questions = [];
  const rowNumbers = [];
  const problems = [];
  let skipped = 0;

  for (let r = headerIdx + 1; r < grid.length; r += 1) {
    const row = grid[r] || [];
    const rowNo = r + 1;                                  // as Excel shows it
    if (row.every((c) => !text(c))) continue;              // blank spacer row
    const qText = text(row[cols.question]);
    if (!qText) {
      problems.push(`Row ${rowNo}: the Question cell is empty.`);
      continue;
    }
    if (qText.toUpperCase().startsWith(EXAMPLE_PREFIX)) { skipped += 1; continue; }

    const options = [];
    cols.options.forEach((c, i) => {
      if (c === -1) return;
      const t = text(row[c]);
      if (t) options.push({ letter: OPTION_LETTERS[i], text: t });
    });

    const { picked, unknown } = resolveCorrect(cols.correct === -1 ? '' : row[cols.correct], options);
    if (unknown.length > 0) {
      problems.push(`Row ${rowNo}: "${unknown[0]}" does not match any option on this row — use the letters of the options you filled in, e.g. A or A, C.`);
      continue;
    }
    if (picked.length === 0) {
      problems.push(`Row ${rowNo}: the Correct Answers cell is empty — mark at least one option.`);
      continue;
    }

    const typeCell = norm(cols.type === -1 ? '' : row[cols.type]);
    let type;
    if (TYPE_SINGLE.includes(typeCell)) type = 'single';
    else if (TYPE_MULTIPLE.includes(typeCell)) type = 'multiple';
    else type = picked.length > 1 ? 'multiple' : 'single';

    const pointsCell = text(cols.points === -1 ? '' : row[cols.points]);
    const points = pointsCell === '' ? 1 : Number(pointsCell);

    questions.push({
      uid: uid(),
      type,
      text: qText,
      points,
      mustPass: TRUTHY.includes(norm(cols.mustPass === -1 ? '' : row[cols.mustPass])),
      options: options.map((o, i) => ({ text: o.text, correct: picked.includes(i) })),
    });
    rowNumbers.push(rowNo);
  }

  if (questions.length === 0 && problems.length === 0) {
    throw new Error(skipped > 0
      ? 'Only the EXAMPLE rows were found — replace them with your own questions.'
      : 'No questions found in the file.');
  }

  return { questions, rowNumbers, skipped, sheetName, problems };
};
